import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ExternalLink, Trash2, CheckCircle2 } from 'lucide-react'
import { useLocationAssignments, usePricingMutations } from '@/hooks/usePricing'
import { mapAssignmentRow, type LocationPricingAssignment } from '@/types/pricing'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatActiveDays(days: number[] | null): string {
  if (!days || days.length === 0 || days.length === 7) return 'All Days'
  if (
    days.length === 5 &&
    [1, 2, 3, 4, 5].every((d) => days.includes(d))
  )
    return 'Mon-Fri'
  if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Sat-Sun'
  return days.map((d) => DAY_NAMES[d]).join(', ')
}

function isTodayActive(assignment: LocationPricingAssignment): boolean {
  const now = new Date()
  const today = now.toISOString().split('T')[0]
  const dow = now.getDay()

  if (!assignment.isActive) return false
  if (assignment.effectiveFrom && today < assignment.effectiveFrom) return false
  if (assignment.effectiveTo && today > assignment.effectiveTo) return false
  if (assignment.activeDays && assignment.activeDays.length > 0 && !assignment.activeDays.includes(dow))
    return false
  return true
}

interface PolicyStackViewProps {
  locationId: string
}

export function PolicyStackView({ locationId }: PolicyStackViewProps) {
  const { data: rawAssignments, isLoading } = useLocationAssignments(locationId)
  const mutations = usePricingMutations()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  // Map raw rows to typed assignments, carry nested policy data through
  const assignments = (rawAssignments ?? []).map((row) => {
    const mapped = mapAssignmentRow(row)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nested = (row as any).pricing_policies as
      | { name: string; scope: string; status: string }
      | undefined
    return { ...mapped, policyName: nested?.name, policyScope: nested?.scope, policyStatus: nested?.status }
  })

  // Find winning policy for today
  const todayWinner = [...assignments]
    .filter(isTodayActive)
    .sort((a, b) => b.priority - a.priority)[0]

  if (assignments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No pricing policies assigned to this location.</p>
        <p className="text-sm mt-1">Assign a policy to start configuring rates.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {assignments.map((a) => {
        const isWinner = todayWinner?.id === a.id
        return (
          <Card key={a.id} className={isWinner ? 'border-primary' : ''}>
            <CardContent className="flex items-center gap-4 py-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{a.policyName ?? 'Unknown Policy'}</span>
                  <Badge variant="outline" className="text-xs">
                    P{a.priority}
                  </Badge>
                  {a.policyScope && (
                    <Badge variant="secondary" className="capitalize text-xs">
                      {a.policyScope}
                    </Badge>
                  )}
                  {isWinner && (
                    <Badge className="text-xs gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Active Today
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span>{formatActiveDays(a.activeDays)}</span>
                  {a.effectiveFrom && <span>From {a.effectiveFrom}</span>}
                  {a.effectiveTo && <span>to {a.effectiveTo}</span>}
                  {!a.isActive && (
                    <Badge variant="outline" className="text-xs text-orange-600">
                      Inactive
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/settings/pricing/policies/${a.policyId}`)}
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  View Rates
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => mutations.removeAssignment.mutate(a.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
